import { useCallback, useEffect, useRef, useState } from "react";

// A floating button the player can drag anywhere on screen (mouse or touch).
// A press that barely moves still counts as a tap and fires onClick.
const POS_KEY = "jangle:result-pill-pos";
const DRAG_THRESHOLD = 6;

function loadPos() {
  try {
    const p = JSON.parse(localStorage.getItem(POS_KEY) || "null");
    if (p && typeof p.x === "number" && typeof p.y === "number") return p;
  } catch {}
  return null;
}

function savePos(p) {
  try { localStorage.setItem(POS_KEY, JSON.stringify(p)); } catch {}
}

export default function DraggablePill({ onClick, className = "", children, ...rest }) {
  const ref = useRef(null);
  const drag = useRef(null);
  const [pos, setPos] = useState(loadPos);
  const [dragging, setDragging] = useState(false);

  const clamp = useCallback((x, y) => {
    const el = ref.current;
    const w = el ? el.offsetWidth : 0;
    const h = el ? el.offsetHeight : 0;
    const m = 8;
    return {
      x: Math.min(Math.max(m, x), Math.max(m, window.innerWidth - w - m)),
      y: Math.min(Math.max(m, y), Math.max(m, window.innerHeight - h - m)),
    };
  }, []);

  // Keep a saved spot on screen after rotation or resize.
  useEffect(() => {
    if (!pos) return undefined;
    const fit = () => setPos((p) => (p ? clamp(p.x, p.y) : p));
    fit();
    window.addEventListener("resize", fit);
    return () => window.removeEventListener("resize", fit);
  }, [clamp, pos === null]); // eslint-disable-line react-hooks/exhaustive-deps

  const onPointerDown = (e) => {
    if (e.button !== undefined && e.button !== 0) return;
    const r = ref.current.getBoundingClientRect();
    drag.current = { id: e.pointerId, sx: e.clientX, sy: e.clientY, ox: r.left, oy: r.top, moved: false };
    try { ref.current.setPointerCapture(e.pointerId); } catch {}
  };

  const onPointerMove = (e) => {
    const d = drag.current;
    if (!d || d.id !== e.pointerId) return;
    const dx = e.clientX - d.sx;
    const dy = e.clientY - d.sy;
    if (!d.moved && Math.hypot(dx, dy) < DRAG_THRESHOLD) return;
    if (!d.moved) { d.moved = true; setDragging(true); }
    e.preventDefault();
    setPos(clamp(d.ox + dx, d.oy + dy));
  };

  const endDrag = (e) => {
    const d = drag.current;
    if (!d || d.id !== e.pointerId) return;
    drag.current = null;
    try { ref.current.releasePointerCapture(e.pointerId); } catch {}
    if (d.moved) {
      setDragging(false);
      setPos((p) => { if (p) savePos(p); return p; });
    } else if (e.type === "pointerup") {
      onClick?.(e);
    }
  };

  const placed = pos
    ? { left: pos.x, top: pos.y }
    : { left: "50%", bottom: 16, transform: "translateX(-50%)" };

  return (
    <button
      type="button"
      ref={ref}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={endDrag}
      onPointerCancel={endDrag}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onClick?.(e); } }}
      style={{ ...placed, touchAction: "none", cursor: dragging ? "grabbing" : "grab" }}
      className={`fixed select-none ${className}`}
      {...rest}
    >
      {children}
    </button>
  );
}
